import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSubscription } from '../../context/SubscriptionContext';
import './PremiumFeatureGate.css';

/**
 * PremiumFeatureGate — Modern, empathetic paywall for premium features.
 * Shows a beautiful teaser when user is not premium; renders children when they are.
 * Aligns with PREMIUM STRATEGY: "I feel seen, prepared, and in control."
 */
function PremiumFeatureGate({
  featureId,
  title,
  description,
  icon: Icon,
  children,
  variant = 'card', // 'card' | 'inline' | 'minimal'
  className = '',
}) {
  const navigate = useNavigate();

  return (
    <motion.div
      className={`premium-feature-gate premium-feature-gate--${variant} ${className}`}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="premium-feature-gate__teaser">
        <div className="premium-feature-gate__glow" aria-hidden="true" />
        <div className="premium-feature-gate__content">
          {Icon && (
            <div className="premium-feature-gate__icon">
              {typeof Icon === 'function' ? <Icon /> : Icon}
            </div>
          )}
          <div className="premium-feature-gate__text">
            <h3 className="premium-feature-gate__title">{title}</h3>
            <p className="premium-feature-gate__description">{description}</p>
          </div>
          <button
            type="button"
            className="premium-feature-gate__cta"
            onClick={() => navigate('/subscription')}
            aria-label={`Unlock ${title} with Premium`}
          >
            <span className="premium-feature-gate__cta-text">Unlock with Premium</span>
            <span className="premium-feature-gate__cta-arrow">→</span>
          </button>
        </div>
        {variant === 'card' && (
          <div className="premium-feature-gate__badge" aria-hidden="true">
            Premium
          </div>
        )}
      </div>
    </motion.div>
  );
}

/**
 * Renders children only when user has premium; otherwise shows PremiumFeatureGate teaser.
 */
export function PremiumOnly({ featureId, title, description, icon, children, variant, className }) {
  const { hasAccess, isPremium } = useSubscription();

  if (hasAccess || isPremium) {
    return <>{children}</>;
  }

  return (
    <PremiumFeatureGate
      featureId={featureId}
      title={title}
      description={description}
      icon={icon}
      variant={variant}
      className={className}
    />
  );
}

export default PremiumFeatureGate;
